import * as React from 'react'
import noop from 'lodash/noop'
import posed from 'react-pose'
import styled from 'styled-components'
import { ImgWithSkeleton } from 'components/Card'
import { makeStyles } from '@material-ui/core/styles'

type Props = {
  item: any
  autoRotationOnFirstView?: boolean
  imgClassName?: string
  imgWrapperClassName?: string
}

const xor = (a: boolean, b: boolean) => Boolean(+a ^ +b)

const IMG_WIDTH = 250
const AUTO_ROTATION_DELAY = 500
const DURATION = 500
const PERSPECTIVE = 600

function useReversable(autoRotationOnFirstView: boolean) {
  const [showBack, setShowBack] = React.useState(false)
  const [rotation, setRotation] = React.useState(0)
  const [isAutoRotating, setIsAutoRotating] = React.useState(false)

  const reverseCard = React.useCallback(
    (isPlusRotation: boolean) => {
      setRotation(rot => (isPlusRotation ? rot + 180 : rot - 180))
      setShowBack(show => !show)
    },
    [autoRotationOnFirstView]
  )

  React.useEffect(() => {
    if (autoRotationOnFirstView)
      setTimeout(() => {
        setIsAutoRotating(true)
        reverseCard(true)
      }, AUTO_ROTATION_DELAY)
  }, [autoRotationOnFirstView])

  const onTransitionEnd = React.useCallback(() => {
    if (autoRotationOnFirstView && isAutoRotating) {
      setIsAutoRotating(false)
      reverseCard(true)
    }
  }, [autoRotationOnFirstView, isAutoRotating])

  const onClick = React.useCallback(
    (isShown: boolean) => (e: React.MouseEvent) => {
      if (isAutoRotating || !isShown) return
      e.persist()
      reverseCard(e.nativeEvent.offsetX > IMG_WIDTH / 2)
    },
    [reverseCard, isAutoRotating]
  )

  return { rotation, showBack, onTransitionEnd, onClick }
}

const Component = ({
  item,
  imgClassName,
  imgWrapperClassName,
  autoRotationOnFirstView,
}: Props) => {
  const { rotation, showBack, onTransitionEnd, onClick } = useReversable(
    autoRotationOnFirstView
  )

  const s = useStyle({})

  return (
    <Content>
      {['front', 'back'].map(type => {
        const isShown = xor(showBack, type === 'front')
        return (
          <PosedImgWithSkeleton
            key={type}
            id={type}
            src={item.thumbnail[type].uri}
            alt="card image"
            imgClassName={`${s.img} ${imgClassName}`}
            imgWrapperClassName={`${s.imgWrapper} ${imgWrapperClassName}`}
            type={type}
            // ImgWithSkeletonの最上位DOM要素に初期状態として渡すstyle
            // アニメーションによるスタイル変化はreact-posedによって付与される
            rootStyle={{
              position: type === 'front' ? 'relative' : 'absolute',
              opacity: type === 'front' ? 1 : 0,
              zIndex: isShown ? 1 : 0,
            }}
            pose={isShown ? 'normal' : 'reverse'}
            isShown={isShown}
            rotation={type === 'back' ? rotation - 180 : rotation}
            onClick={onClick(isShown)}
            onPoseComplete={!isShown ? onTransitionEnd : noop}
            aspectRatio={1.285}
          />
        )
      })}
    </Content>
  )
}

const useStyle = makeStyles(theme => ({
  img: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0 /* Make the picture taking the size of it's parent */,
    width: '100%' /* This if for the object-fit */,
    height: '100%' /* This if for the object-fit */,
    objectFit: 'contain',
    objectPosition: 'center',
  },
  imgWrapper: {
    width: '100%',
    height: '100%',
  },
}))

const Content = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: IMG_WIDTH,
  perspective: `${PERSPECTIVE}px`,
})

const PosedImgWithSkeleton = posed(ImgWithSkeleton)({
  normal: {
    transform: ({ rotation }) => `rotateY(${rotation}deg)`,
    opacity: 1,
    transition: {
      transform: { duration: DURATION },
      opacity: ({ from, to }) => ({
        type: 'keyframes',
        values: [from, from, to, to],
        times: [0, 0.49, 0.5, 1],
      }),
    },
  },
  reverse: {
    transform: ({ rotation }) => `rotateY(${rotation}deg)`,
    opacity: 0,
    transition: {
      transform: { duration: DURATION },
      opacity: ({ from, to }) => ({
        type: 'keyframes',
        values: [from, from, to, to],
        times: [0, 0.49, 0.5, 1],
      }),
    },
  },
})

export default Component
